

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Webcam from "react-webcam";
import { Button, message } from 'antd';
import successScreen from "../../assets/kycsuccess.png";
import failScreen from "../../assets/kycFail.png";
import axios from 'axios';
import _config from '../../config/api.config';
import { useDispatch } from 'react-redux';
import { setLoader } from "../../state/slices/loader";
import Sdloader from '../../components/Loader/FullPageLoader';

const VideoKyc = () => {
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const countdownRef = useRef(null);
  const startTimeRef = useRef(null);
  const elapsedTimeRef = useRef(0);
  const dispatch = useDispatch();
  const [userData, setUserData] = useState("");
  const [capturing, setCapturing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [screenView, setScreenView] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const refId = queryParams.get('refId');

  const videoConstraints = { facingMode: "user" };
  const audioConstraints = {
    suppressLocalAudioPlayback: true,
    noiseSuppression: true,
    echoCancellation: true,
  };

  const startCountdown = () => {
    countdownRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(countdownRef.current);
          handleStopCaptureClick();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  const handleStartCaptureClick = useCallback(() => {
    setCapturing(true);
    setPaused(false);
    setTimeLeft(20);
    elapsedTimeRef.current = 0;
    startTimeRef.current = Date.now();

    if (webcamRef.current?.stream) {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        mimeType: "video/webm"
      });
      mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
      mediaRecorderRef.current.start();
      startCountdown();
    } else {
      message.error("Webcam not available");
    }
  }, [webcamRef]);

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => Array.isArray(prev) ? prev.concat(data) : [data]);
    }
  }, []);

  const handleStopCaptureClick = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setCapturing(false);
    clearInterval(countdownRef.current);
    setTimeLeft(20);
  }, []);

  const handlePauseCaptureClick = useCallback(() => {
    if (!mediaRecorderRef.current) return;

    if (mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
      setPaused(true);
      clearInterval(countdownRef.current);
      elapsedTimeRef.current += (Date.now() - startTimeRef.current) / 1000;
    } else if (mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
      setPaused(false);
      startTimeRef.current = Date.now();
      startCountdown();
    }
  }, []);

  const handleRetakeClick = useCallback(() => {
    setRecordedChunks([]);
    setTimeLeft(20);
    setCapturing(false);
    setPaused(false);
    clearInterval(countdownRef.current);
  }, []);

  const handleDownload = useCallback(() => {
    if (Array.isArray(recordedChunks) && recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style.display = "none";
      a.href = url;
      a.download = "react-webcam-stream-capture.webm";
      a.click();
      window.URL.revokeObjectURL(url);
    }
  }, [recordedChunks]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${_config.baseURL}/proposal/expose/getVideoKycDetail`, {
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          },
          params: { refId }
        });

        setUserData(response.data);
        setRecordedChunks([]);
      } catch (error) {
        console.error('Error fetching data:', error);
        message.error('Error fetching user data');
      }
    };

    fetchData();
  }, [token, refId]);

  const handleSubmit = useCallback(async () => {
    if (!Array.isArray(recordedChunks) || recordedChunks.length === 0) {
      message.error("No video recorded");
      return;
    }

    const blob = new Blob(recordedChunks, { type: "video/webm" });
    const formData = new FormData();
    formData.append('video', blob, 'video.webm');
    formData.append('otp', userData?.data?.kycDetail?.otp || "");
    formData.append('proposalId', userData?.data?.proposalDetail?._id || "");
    setLoading(true);
    dispatch(setLoader(true));
    try {
      await axios.post(`${_config.baseURL}/service/karza/kyc/video/request`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      setLoading(false);
      dispatch(setLoader(false));

      message.success('Video uploaded successfully');
      setScreenView(true);
      setRecordedChunks([]);
    } catch (error) {
      setLoading(false);
      dispatch(setLoader(false));

      setScreenView(null);
      console.error('Error uploading video:', error);
      message.error('Error uploading video');
    }
  }, [recordedChunks, userData, token, dispatch]);
  return (
    <>
      {loading && <Sdloader sdloader={loading} />}
      {screenView === false ? (
        <div>
          <div style={{ padding: 15 }}>
            <h5 style={{
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              color: "#003399",
              fontWeight: 'bold',
              fontSize: 23,
              padding: "10px 0px"
            }}>
              Video KYC
            </h5>

            <div className='video-width'>
              <Webcam
                muted
                style={{ width: '100%', height: 'auto' }}
                audio
                ref={webcamRef}
                videoConstraints={videoConstraints}
                audioConstraints={audioConstraints}
              />
            </div>

            <div style={{
              padding: "10px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              justifyContent: "center"
            }}>
              <div style={{ fontSize: 16, fontWeight: "bold", color: "#003399" }}>The video will stop in:</div>
              <div style={{ fontSize: 23, fontWeight: "bold", color: "#003399" }}>{timeLeft === 0 ? "0:00" : timeLeft}</div>
            </div>

            <div style={{
              padding: "10px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
              justifyContent: "center"
            }}>
              <div style={{ fontSize: 16, fontWeight: "bold", color: "#003399" }}>
                Kindly Read This OTP :
              </div>
              <div style={{ fontSize: 26, fontWeight: "bold", color: "#003399" }}>
                {userData?.data?.kycDetail?.otp || "----"}
              </div>
            </div>

            <div style={{
              padding: "20px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: 5
            }}>
              {capturing ? (
                <>
                  <Button className='button-video1' onClick={handlePauseCaptureClick}>
                    {paused ? 'Resume Capture' : 'Pause Capture'}
                  </Button>
                  <Button className='button-video1' onClick={handleStopCaptureClick}>Stop Capture</Button>
                  <Button className='button-video1' onClick={handleRetakeClick}>Retake</Button>
                </>
              ) : (
                <Button className='button-video1' onClick={handleStartCaptureClick}>Start Capture</Button>
              )}
              {Array.isArray(recordedChunks) && recordedChunks.length > 0 && (
                <Button style={{ marginLeft: 5 }} className='button-video' onClick={handleDownload}>Download</Button>
              )}
            </div>

            <div style={{ margin: "5px 20px" }}>
              {Array.isArray(recordedChunks) && recordedChunks.length > 0 && (
                <Button style={{ width: "100%" }} onClick={handleSubmit} className='button-video1'>Submit</Button>
              )}
            </div>
          </div>
        </div>
      ) : screenView === true ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
          alignItems: "center",
          justifyContent: "center",
          marginTop: "50%"
        }}>
          <img src={successScreen} alt="success" />
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#003399" }}>Thank you for the Video KYC</div>
        </div>
      ) : (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 30,
          alignItems: "center",
          justifyContent: "center",
          marginTop: "50%"
        }}>
          <img src={failScreen} alt="failed" />
          <div style={{ fontSize: "18px", fontWeight: "bold", color: "#003399" }}>Video KYC Failed</div>
        </div>
      )}
    </>
  );
};

export default VideoKyc;
